import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Pencil, Eye, AlertTriangle } from 'lucide-react';
import { Combobox } from '../components/Combobox';
import { FilterField } from '../components/FilterField';
import CompatSyncModal from '../components/CompatSyncModal';
import MachineDetailModal from '../components/MachineDetailModal';
import MarkRebrandsReviewedModal from '../components/MarkRebrandsReviewedModal';
import { CatEscluseBadge } from '../components/CatEscluseBadge';
import { useDebounce } from '../../../../hooks/useDebounce';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { useAbbinatoreFacets } from '../hooks/useAbbinatoreFacets';
import { useColumnWidths } from '../hooks/useColumnWidths';
import {
  fetchMarche, fetchTipologie, fetchRebrandBrands, fetchRebrandBrandsPref, saveRebrandBrandsPref, fetchCompatibilita,
  MarcaOption, CompatRow, RebrandChild, MachineRow,
} from '../api';

interface SyncTarget {
  baseId: string; baseModel: string; baseBrand: string;
  rebrandId: string; rebrandModel: string; rebrandBrand: string;
  categorieEscluse: string[];
}

interface ReviewTarget {
  baseId: string; baseModel: string; baseBrand: string;
  staleChildren: { id: string; brand: string; model: string }[];
}

const PAGE_SIZE = 100;
const ROW_HEIGHT = 42;

// Larghezze colonne predefinite (px) — ridimensionabili dall'header,
// persistite in localStorage (le colonne rebrand per singola marca).
const WIDTHS_KEY = 'abbinatore-v2-compat-col-widths';
const W_NOME = 250;
const W_TOTA = 100;
const W_REBRAND_MODEL = 215;
const W_REBRAND_TOTA = 90;
const W_REBRAND_CATE = 115;
const W_ULT = 150;

const SEP = 'border-r-2 border-[#d8d8d4]';

// Maniglia di ridimensionamento sul bordo destro di una cella header
// (la cella deve essere posizionata: relative o sticky).
function ResizeHandle({ testId, onPointerDown, onDoubleClick }: {
  testId: string;
  onPointerDown: (e: React.PointerEvent) => void;
  onDoubleClick: () => void;
}) {
  return (
    <div
      onPointerDown={onPointerDown}
      onDoubleClick={onDoubleClick}
      data-testid={testId}
      title="Trascina per ridimensionare · doppio click per ripristinare"
      className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-[#2b3a8f]/30 active:bg-[#2b3a8f]/50"
    />
  );
}

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// Vero se la macchina produttore ha un'ultima associazione più recente del
// modello rebrand — segnala che il rebrand potrebbe essere "indietro".
// La review (`reviewedAt`) sospende la segnalazione finché la base non
// riceve ULTERIORI associazioni successive al momento della review.
const isStale = (baseLastAssoc: string | null, child: RebrandChild | undefined) => {
  if (!child || !baseLastAssoc) return false;
  const baseTime = new Date(baseLastAssoc).getTime();
  const childTime = child.lastAssoc ? new Date(child.lastAssoc).getTime() : -Infinity;
  if (baseTime <= childTime) return false;
  if (!child.reviewedAt) return true;
  return baseTime > new Date(child.reviewedAt).getTime();
};

/**
 * Vista "Compatibilità": per una marca selezionata, mostra ogni macchina
 * con le sue associazioni totali affiancate — per ciascuna marca rebrand
 * auto-rilevata — al modello corrispondente, alle sue associazioni e al
 * numero di categorie escluse dalla relazione di rebrand.
 */
export default function CompatibilitaView() {
  // Filtri obbligatori
  const [brand, setBrand] = useState<string | null>(null);
  const [tipo, setTipo] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filtro, setFiltro] = useState<'tutte' | 'con_similari' | 'da_aggiornare'>('tutte');
  const debouncedSearch = useDebounce(search, 300);

  // Filtri in cascata dalla marca.
  const { marche, tipologie } = useAbbinatoreFacets('machines', brand, {
    value: tipo, onChange: setTipo,
  });
  // Liste complete per la scheda macchina (modifica marca/tipologia).
  const [tutteMarche, setTutteMarche] = useState<MarcaOption[]>([]);
  const [tutteTipologie, setTutteTipologie] = useState<{ code: string; label: string }[]>([]);

  // Filtro speciale: marche rebrand auto-popolate dalla marca selezionata
  const [rebrandAvailable, setRebrandAvailable] = useState<string[]>([]);
  const [rebrandSelected, setRebrandSelected] = useState<string[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Dati tabella
  const [rows, setRows] = useState<CompatRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  // Modal di sincronizzazione articoli produttore ↔ rebrand
  const [syncTarget, setSyncTarget] = useState<SyncTarget | null>(null);

  // Modal di conferma "Segna come aggiornati" (rebrand da_aggiornare di una riga)
  const [reviewTarget, setReviewTarget] = useState<ReviewTarget | null>(null);

  // Modal di dettaglio macchina (riuso dello stesso componente della tab Elenco)
  const [detailMachine, setDetailMachine] = useState<MachineRow | null>(null);

  const { widthOf, startResize, resetWidth } = useColumnWidths(WIDTHS_KEY);
  const wNome = widthOf('nome', W_NOME);
  const wTota = widthOf('tota', W_TOTA);
  const wUlt = widthOf('ult', W_ULT);
  const wRebrandModel = (b: string) => widthOf(`${b}:model`, W_REBRAND_MODEL);
  const wRebrandTota = (b: string) => widthOf(`${b}:tota`, W_REBRAND_TOTA);
  const wRebrandCate = (b: string) => widthOf(`${b}:cate`, W_REBRAND_CATE);
  const resizeHandle = (id: string, width: number) => (
    <ResizeHandle
      testId={`compat-resize-${id}`}
      onPointerDown={e => startResize(id, width, e)}
      onDoubleClick={() => resetWidth(id)}
    />
  );

  // Ricarica le righe correntemente caricate (0..page) senza resettare lo scroll,
  // usata dopo una sincronizzazione riuscita per aggiornare i conteggi in tabella.
  const reloadVisibleRows = () => {
    if (!brand) return;
    Promise.all(
      Array.from({ length: page + 1 }, (_, i) =>
        fetchCompatibilita({ brand, tipo: tipo || undefined, search: debouncedSearch || undefined, filtro: filtro === 'tutte' ? undefined : filtro, page: i, pageSize: PAGE_SIZE })
      )
    ).then(results => {
      setRows(results.flatMap(r => r.items));
      setTotal(results[results.length - 1].total);
    });
  };

  useEffect(() => {
    fetchMarche().then(setTutteMarche).catch(() => {});
    fetchTipologie().then(setTutteTipologie).catch(() => {});
  }, []);

  // Quando cambia la marca base: ricarica le marche rebrand auto-popolate,
  // poi applica la selezione SALVATA per questa marca (se presente) —
  // altrimenti default a tutte le marche disponibili.
  useEffect(() => {
    if (!brand) { setRebrandAvailable([]); setRebrandSelected([]); return; }
    fetchRebrandBrands(brand)
      .then(brands => {
        setRebrandAvailable(brands);
        fetchRebrandBrandsPref(brand)
          .then(saved => {
            if (saved && saved.length > 0) {
              setRebrandSelected(saved.filter(b => brands.includes(b)));
            } else {
              setRebrandSelected(brands);
            }
          })
          .catch(() => setRebrandSelected(brands));
      })
      .catch(() => { setRebrandAvailable([]); setRebrandSelected([]); });
  }, [brand]);

  const filterSig = useMemo(() => JSON.stringify([brand, tipo, debouncedSearch, filtro]), [brand, tipo, debouncedSearch, filtro]);

  useEffect(() => {
    setRows([]); setPage(0); setHasMore(true);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [filterSig]);

  // Con poche righe (poco/nessun overflow verticale) e molte colonne rebrand
  // (overflow orizzontale), la classica rotella del mouse (solo verticale)
  // non permette di scoprire lo scroll orizzontale — se non c'è margine di
  // scroll verticale, traduciamo la rotella in scroll orizzontale.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (e.shiftKey || e.deltaY === 0) return;
      const noVerticalRoom = el.scrollHeight <= el.clientHeight + 1;
      if (noVerticalRoom && el.scrollWidth > el.clientWidth) {
        el.scrollLeft += e.deltaY;
        e.preventDefault();
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [rows.length, rebrandSelected.length]);

  // Altezza reale della riga header, usata come offset "top" per la riga
  // sticky del gruppo (deve incollarsi appena sotto l'header, non sotto).
  // Ri-misura quando `brand` cambia: l'header esiste nel DOM solo dopo la
  // selezione di una marca (è dentro il ramo condizionale `{brand ? ... }`).
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const measure = () => setHeaderHeight(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [brand]);

  useEffect(() => {
    if (!brand) { setRows([]); setTotal(0); setHasMore(false); return; }
    let cancel = false;
    setLoading(true);
    fetchCompatibilita({ brand, tipo: tipo || undefined, search: debouncedSearch || undefined, filtro: filtro === 'tutte' ? undefined : filtro, page, pageSize: PAGE_SIZE })
      .then(r => {
        if (cancel) return;
        setRows(prev => page === 0 ? r.items : [...prev, ...r.items]);
        setTotal(r.total);
        setHasMore((page + 1) * PAGE_SIZE < r.total && r.items.length > 0);
      })
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [filterSig, page]); // eslint-disable-line react-hooks/exhaustive-deps

  useInfiniteScroll(sentinelRef, () => {
    if (!loading && hasMore) setPage(p => p + 1);
  }, hasMore && !loading);

  const brandOpts = useMemo(() => marche.map(m => ({ code: m.code, label: m.label })), [marche]);

  // Costruisce un MachineRow "compatibile" con MachineDetailModal (riuso dello
  // stesso componente della tab Elenco) a partire dai dati già disponibili
  // in questa vista — evita una chiamata API dedicata.
  const toMachineRow = (
    id: string, model: string, mBrand: string, mType: string | null,
    compat: number, lastAssoc: string | null, hasParent: boolean, rebrandFrom: string | null,
  ): MachineRow => ({
    id, model, brand: mBrand, brandCode: null, type: mType, series: null,
    compat, lastAssoc, hasParent, rebrandFrom, rebrandModel: null,
  });

  const removeRebrand = (b: string) => setRebrandSelected(prev => {
    const next = prev.filter(x => x !== b);
    if (brand) saveRebrandBrandsPref(brand, next).catch(() => {});
    return next;
  });
  const addRebrand = (b: string) => setRebrandSelected(prev => {
    const next = [...prev, b];
    if (brand) saveRebrandBrandsPref(brand, next).catch(() => {});
    return next;
  });
  const availableToAdd = rebrandAvailable.filter(b => !rebrandSelected.includes(b));

  // Rebrand "da aggiornare" di una riga, su TUTTE le marche rebrand (non solo
  // quelle attualmente selezionate come colonne) — usato per l'icona di riga.
  const staleChildrenOf = (row: CompatRow) => {
    const out: { id: string; brand: string; model: string }[] = [];
    Object.values(row.rebrands).forEach(children => {
      children.forEach(child => {
        if (isStale(row.lastAssoc, child)) out.push({ id: child.id, brand: child.brand, model: child.model });
      });
    });
    return out;
  };

  const totalWidth = wNome + wTota + rebrandSelected.reduce((sum, b) => sum + wRebrandModel(b) + wRebrandTota(b) + wRebrandCate(b), 0) + wUlt;

  return (
    <div className="flex flex-col h-full" data-testid="compatibilita-view">
      {/* Filtri obbligatori orizzontali compatti (label a lato) */}
      <div className="flex items-center gap-4 flex-wrap bg-white border border-[#e2e2df] rounded-[10px] px-3.5 py-2.5 mb-3.5 flex-none">
        <FilterField label="Marca" className="w-[300px] flex-none">
          <Combobox
            value={brand}
            onChange={setBrand}
            options={brandOpts}
            placeholder="Seleziona marca…"
            testId="compat-filter-brand"
            emptyText="Nessuna marca"
          />
        </FilterField>
        <FilterField label="Tipologia" className="w-[210px] flex-none">
          <select
            value={tipo ?? ''}
            onChange={e => setTipo(e.target.value || null)}
            data-testid="compat-filter-tipo"
            className="w-full px-2.5 py-[6px] border border-[#d8d8d4] rounded-[7px] text-[14px] bg-white focus:outline-none focus:border-[#2b3a8f]"
          >
            <option value="">Tutte</option>
            {tipologie.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
          </select>
        </FilterField>
        <FilterField label="Cerca modello" className="w-[190px] flex-none">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Nome modello…"
            data-testid="compat-filter-search"
            className="w-full px-2.5 py-[6px] border border-[#d8d8d4] rounded-[7px] text-[14px] focus:outline-none focus:border-[#2b3a8f]"
          />
        </FilterField>
        <FilterField label="Vista" className="flex-none">
          <div className="flex items-center gap-3">
            {([
              { value: 'tutte', label: 'Tutte' },
              { value: 'con_similari', label: 'Con similari' },
              { value: 'da_aggiornare', label: 'Da aggiornare' },
            ] as { value: typeof filtro; label: string }[]).map(opt => (
              <label
                key={opt.value}
                className="flex items-center gap-1.5 text-[14px] text-[#3a3a36] cursor-pointer whitespace-nowrap"
                data-testid={`compat-filter-view-${opt.value}`}
              >
                <input
                  type="radio"
                  name="compat-view-filter"
                  checked={filtro === opt.value}
                  onChange={() => setFiltro(opt.value)}
                  className="accent-[#2b3a8f] w-3.5 h-3.5"
                />
                {opt.label}
              </label>
            ))}
          </div>
        </FilterField>
      </div>

      {/* Filtro speciale: marche rebrand auto-popolate */}
      {brand && (
        <div className="flex items-center gap-2 flex-wrap bg-white border border-[#e2e2df] rounded-[11px] p-3 mb-3.5 flex-none" data-testid="compat-rebrand-filter">
          <span className="text-[13px] font-semibold text-[#5c5c57]">Marche rebrand:</span>
          {rebrandAvailable.length === 0 ? (
            <span className="text-[13px] text-[#5c5c57]">nessuna marca rebrand trovata per {brand}</span>
          ) : (
            <>
              {rebrandSelected.map(b => (
                <span
                  key={b}
                  data-testid={`compat-rebrand-chip-${b}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#eef1fb] border border-[#d4dcf7] rounded-[7px] text-[13px] font-semibold text-[#2b3a8f]"
                >
                  {b}
                  <button onClick={() => removeRebrand(b)} data-testid={`compat-rebrand-remove-${b}`} className="hover:opacity-70">✕</button>
                </span>
              ))}
              <div className="relative inline-block">
                <button
                  onClick={() => setPickerOpen(o => !o)}
                  disabled={availableToAdd.length === 0}
                  data-testid="compat-rebrand-add"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-dashed border-[#c4c4c1] rounded-[7px] text-[13px] text-[#5c5c57] hover:border-[#2b3a8f] hover:text-[#2b3a8f] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Plus className="w-3 h-3" /> aggiungi marca
                </button>
                {pickerOpen && (
                  <div className="absolute z-50 left-0 top-full mt-1 bg-white border border-[#d8d8d4] rounded-[8px] shadow-lg min-w-[180px] py-1">
                    {availableToAdd.map(b => (
                      <button
                        key={b}
                        onClick={() => { addRebrand(b); setPickerOpen(false); }}
                        data-testid={`compat-rebrand-option-${b}`}
                        className="w-full text-left px-3 py-1.5 text-[14px] text-[#3a3a36] hover:bg-[#fafafa]"
                      >
                        {b}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {!brand ? (
        <div className="flex-1 flex items-center justify-center text-[15px] text-[#5c5c57] bg-white border border-[#e2e2df] rounded-[11px]" data-testid="compat-no-brand">
          Seleziona una marca per visualizzare la vista di compatibilità.
        </div>
      ) : (
        <div className="bg-white border border-[#e2e2df] rounded-[11px] overflow-hidden flex flex-col flex-1 min-h-0">
          <div ref={scrollRef} className="flex-1 overflow-auto" data-testid="compat-scroll">
            <div style={{ width: totalWidth, minWidth: '100%' }}>
              {/* Header */}
              <div ref={headerRef} className="flex bg-[#fafafa] border-b border-[#e4e4e1] sticky top-0 z-10">
                <div
                  style={{ width: wNome }}
                  className="flex-none py-2.5 px-3 text-[11px] font-bold tracking-[0.08em] text-[#5c5c57] font-mono sticky left-0 z-20 bg-[#fafafa]"
                >
                  NOME
                  {resizeHandle('nome', wNome)}
                </div>
                <div style={{ width: wTota, left: wNome }} className={`flex-none py-2.5 px-3 text-[11px] font-bold tracking-[0.08em] text-[#5c5c57] font-mono text-right sticky z-20 bg-[#fafafa] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)] ${SEP}`}>
                  TOT.A
                  {resizeHandle('tota', wTota)}
                </div>
                {rebrandSelected.map(b => (
                  <React.Fragment key={b}>
                    <div style={{ width: wRebrandModel(b) }} className="relative flex-none py-2.5 px-3 text-[15px] font-extrabold text-[#2b3a8f] truncate" title={b}>
                      {b}
                      {resizeHandle(`${b}:model`, wRebrandModel(b))}
                    </div>
                    <div style={{ width: wRebrandTota(b) }} className="relative flex-none py-2.5 px-3 text-[11px] font-bold tracking-[0.08em] text-[#5c5c57] font-mono text-right">
                      TOT.A
                      {resizeHandle(`${b}:tota`, wRebrandTota(b))}
                    </div>
                    <div style={{ width: wRebrandCate(b) }} className={`relative flex-none py-2.5 px-3 text-[11px] font-bold tracking-[0.08em] text-[#5c5c57] font-mono text-right ${SEP}`}>
                      CAT.E
                      {resizeHandle(`${b}:cate`, wRebrandCate(b))}
                    </div>
                  </React.Fragment>
                ))}
                <div style={{ width: wUlt }} className="relative flex-none py-2.5 px-3 text-[11px] font-bold tracking-[0.08em] text-[#5c5c57] font-mono text-right">
                  ULT.ABBIN.
                  {resizeHandle('ult', wUlt)}
                </div>
              </div>

              {rows.length === 0 && !loading ? (
                <div className="px-4 py-16 text-center text-[14px] text-[#5c5c57]" data-testid="compat-empty">Nessuna macchina trovata</div>
              ) : (
                <>
                  {rows.map(row => {
                    const groupSize = Math.max(1, ...rebrandSelected.map(b => row.rebrands[b]?.length ?? 0));
                    const isGroup = groupSize > 1;
                    const staleChildren = staleChildrenOf(row);
                    return (
                      <div key={row.id} data-testid={`compat-group-${row.id}`} className="flex items-start">
                        {/* Colonna NOME congelata: un riquadro di sfondo copre l'intera
                            estensione verticale del gruppo (anche le righe "vuote" dei
                            rebrand), mentre il testo/le icone restano ancorati in alto
                            (sticky) — il resto della riga (TOT.A, colonne rebrand,
                            ULT.ABBIN.) scorre sempre libero in verticale. */}
                        <div
                          style={{ width: wNome, height: groupSize * ROW_HEIGHT }}
                          className={`flex-none sticky left-0 z-[3] bg-[#f7f7f5] ${isGroup ? 'border-t-2 border-b-2 border-[#c4c4c1]' : 'border-b border-[#f1f1ee]'}`}
                        >
                          <div
                            style={{ height: ROW_HEIGHT, top: headerHeight }}
                            data-testid={`compat-nome-${row.id}`}
                            className="sticky px-3 flex items-center gap-1.5 text-[14px] font-bold text-[#1f1f1d] hover:bg-[#efefec]"
                          >
                            <span className="truncate">{row.model}</span>
                            {staleChildren.length > 0 && (
                              <button
                                onClick={() => setReviewTarget({ baseId: row.id, baseModel: row.model, baseBrand: row.brand, staleChildren })}
                                data-testid={`compat-review-open-${row.id}`}
                                title={`${staleChildren.length} rebrand da aggiornare — clicca per segnare come aggiornati`}
                                className="flex-none text-amber-500 hover:text-amber-600 transition-colors"
                              >
                                <AlertTriangle className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <button
                              onClick={() => setDetailMachine(toMachineRow(row.id, row.model, row.brand, row.type, row.compat, row.lastAssoc, false, null))}
                              data-testid={`compat-detail-open-${row.id}`}
                              title="Vedi dettaglio macchina"
                              className="flex-none text-[#9b9b96] hover:text-[#2b3a8f] transition-colors"
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>

                        {/* Colonna TOT.A congelata: stesso trattamento di NOME — si
                            muove insieme al nome del modello (sticky sia orizzontale
                            che verticale per l'intera estensione del gruppo). */}
                        <div
                          style={{ width: wTota, height: groupSize * ROW_HEIGHT, left: wNome }}
                          className={`flex-none sticky z-[3] bg-[#f7f7f5] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)] ${SEP} ${isGroup ? 'border-t-2 border-b-2 border-[#c4c4c1]' : 'border-b border-[#f1f1ee]'}`}
                        >
                          <div
                            style={{ height: ROW_HEIGHT, top: headerHeight }}
                            data-testid={`compat-tota-${row.id}`}
                            className="sticky px-3 flex items-center justify-end text-[14px] font-semibold font-mono text-[#2b3a8f] hover:bg-[#efefec]"
                          >
                            {row.compat}
                          </div>
                        </div>

                        <div className="flex flex-col flex-1 min-w-0">
                          {Array.from({ length: groupSize }, (_, i) => {
                            const isFirst = i === 0;
                            const isLast = i === groupSize - 1;
                            return (
                              <div
                                key={`${row.id}-${i}`}
                                data-testid={`compat-row-${row.id}-${i}`}
                                style={{ height: ROW_HEIGHT }}
                                className={`group flex items-center hover:bg-[#fafafa] ${isGroup && isFirst ? 'border-t-2 border-[#c4c4c1]' : ''} ${isLast && isGroup ? 'border-b-2 border-[#c4c4c1]' : 'border-b border-[#f1f1ee]'}`}
                              >
                                {rebrandSelected.map(b => {
                                  const child = row.rebrands[b]?.[i];
                                  const stale = isStale(row.lastAssoc, child);
                                  return (
                                    <React.Fragment key={b}>
                                      <div
                                        style={{ width: wRebrandModel(b) }}
                                        data-testid={stale && child ? `compat-stale-${child.id}` : undefined}
                                        className={`flex-none px-3 flex items-center gap-1.5 text-[14px] font-bold truncate ${stale ? 'bg-amber-50 text-amber-800' : 'text-[#3a3a36]'}`}
                                        title={stale ? `${child?.model} — ultima associazione meno recente del modello produttore` : child?.model}
                                      >
                                        <span className="truncate">{child?.model ?? ''}</span>
                                        {child && (
                                          <>
                                            <button
                                              onClick={() => setSyncTarget({
                                                baseId: row.id, baseModel: row.model, baseBrand: brand!,
                                                rebrandId: child.id, rebrandModel: child.model, rebrandBrand: b,
                                                categorieEscluse: child.categorieEscluse,
                                              })}
                                              data-testid={`compat-sync-open-${child.id}`}
                                              title="Confronta e sincronizza articoli"
                                              className="flex-none text-[#9b9b96] hover:text-[#2b3a8f] transition-colors"
                                            >
                                              <Pencil className="w-3.5 h-3.5" />
                                            </button>
                                            <button
                                              onClick={() => setDetailMachine(toMachineRow(child.id, child.model, child.brand, child.type, child.compat, child.lastAssoc, true, row.model))}
                                              data-testid={`compat-detail-open-${child.id}`}
                                              title="Vedi dettaglio macchina"
                                              className="flex-none text-[#9b9b96] hover:text-[#2b3a8f] transition-colors"
                                            >
                                              <Eye className="w-3.5 h-3.5" />
                                            </button>
                                          </>
                                        )}
                                      </div>
                                      <div style={{ width: wRebrandTota(b) }} className={`flex-none truncate px-3 text-[14px] font-mono text-right ${stale ? 'bg-amber-50 text-amber-700' : 'text-[#5c5c57]'}`}>{child ? child.compat : ''}</div>
                                      <div style={{ width: wRebrandCate(b) }} className={`flex-none overflow-hidden px-3 flex items-center justify-end ${SEP} ${stale ? 'bg-amber-50' : ''}`}>
                                        {child && <CatEscluseBadge cats={child.categorieEscluse} testId={`compat-catE-${child.id}`} />}
                                      </div>
                                    </React.Fragment>
                                  );
                                })}
                                <div style={{ width: wUlt }} className="flex-none truncate px-3 text-[13px] font-mono text-[#5c5c57] text-right">{isFirst ? fmtDate(row.lastAssoc) : ''}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  <div ref={sentinelRef} className="h-8 flex items-center justify-center text-[13px] font-mono text-[#5c5c57]">
                    {loading
                      ? 'Caricamento…'
                      : hasMore
                      ? '↓ scorri per caricare altri'
                      : rows.length > 0 ? `fine elenco · ${total.toLocaleString('it')} risultati` : ''}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {syncTarget && (
        <CompatSyncModal
          baseId={syncTarget.baseId}
          baseModel={syncTarget.baseModel}
          baseBrand={syncTarget.baseBrand}
          rebrandId={syncTarget.rebrandId}
          rebrandModel={syncTarget.rebrandModel}
          rebrandBrand={syncTarget.rebrandBrand}
          categorieEscluse={syncTarget.categorieEscluse}
          onClose={() => setSyncTarget(null)}
          onSynced={reloadVisibleRows}
        />
      )}

      {detailMachine && (
        <MachineDetailModal
          machine={detailMachine}
          onClose={() => setDetailMachine(null)}
          marche={tutteMarche}
          tipologie={tutteTipologie}
          onMachineUpdated={reloadVisibleRows}
        />
      )}

      {reviewTarget && (
        <MarkRebrandsReviewedModal
          baseId={reviewTarget.baseId}
          baseModel={reviewTarget.baseModel}
          baseBrand={reviewTarget.baseBrand}
          staleChildren={reviewTarget.staleChildren}
          onClose={() => setReviewTarget(null)}
          onReviewed={reloadVisibleRows}
        />
      )}
    </div>
  );
}
